import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, Clock, AlertTriangle, ChevronRight } from "lucide-react";
import { groundOpsApi } from "@/api";

const RISK_STYLE = {
  on_time:  "bg-green-500/10 border-green-500/25 text-green-400",
  at_risk:  "bg-amber-500/10 border-amber-500/25 text-amber-400",
  delayed:  "bg-red-500/10 border-red-500/30 text-red-400",
  arriving: "bg-sky-500/10 border-sky-500/25 text-sky-400",
};

export default function RampFlightBoard({ user }) {
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    groundOpsApi.listFlights({ station: user.station })
      .then(setFlights)
      .finally(() => setLoading(false));
  }, [user.station]);

  if (loading) return <div className="p-6 text-center text-slate-500 animate-pulse">Loading flights...</div>;

  const active = flights.filter(f => !["departed"].includes(f.ground_status));

  return (
    <div className="p-4">
      <div className="mb-4">
        <h2 className="text-base font-black text-white">Flights at {user.station}</h2>
        <p className="text-[11px] text-slate-500">{active.length} active</p>
      </div>

      {active.length === 0 && (
        <div className="text-center py-12 text-slate-600">
          <CheckCircle size={40} className="mx-auto mb-3 opacity-20" />
          <p className="text-sm">No active flights at this station</p>
        </div>
      )}

      <div className="space-y-3">
        {active.map(f => {
          const pct = Math.round(f.tasks_completed / Math.max(1, f.tasks_total) * 100);
          const rs  = f.risk_status || "on_time";
          return (
            <div
              key={f.id}
              onClick={() => navigate(`/ramp/flight-tasks/${f.id}`)}
              className="bg-[#0c1929] border border-slate-700/30 rounded-2xl p-4 cursor-pointer active:scale-98 transition-transform"
              data-testid={`ramp-flight-${f.flight_number.replace(" ","")}`}
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-black text-lg font-mono text-white">{f.flight_number}</span>
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border uppercase ${RISK_STYLE[rs] || RISK_STYLE.on_time}`}>
                      {f.delay_risk_label || rs}
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {f.origin} → {f.destination} · {f.aircraft_reg}
                    {f.stand && ` · Stand ${f.stand}`}
                  </div>
                </div>
                <ChevronRight size={18} className="text-slate-600 mt-1" />
              </div>

              <div className="mt-2">
                <div className="flex justify-between text-[10px] text-slate-500 mb-1">
                  <span>{f.tasks_completed}/{f.tasks_total} tasks</span>
                  <span>{pct}%</span>
                </div>
                <div className="h-2 bg-slate-700/50 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${rs === "delayed" ? "bg-red-500" : rs === "at_risk" ? "bg-amber-500" : "bg-green-500"}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>

              {f.current_bottleneck && (
                <div className="text-[10px] text-amber-400/80 mt-1.5 flex items-center gap-1">
                  <AlertTriangle size={9} /> {f.current_bottleneck}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
